import ApolloClient from 'apollo-client'
import { createHttpLink } from 'apollo-link-http'
import { gql } from 'apollo-boost'
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory'
import { onError } from 'apollo-link-error'
import { getEncodedOrigin } from './origin'

const httpLink = createHttpLink({
  uri: 'https://platform.caroda.io/api/gql',
  credentials: 'include',
})

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (
    graphQLErrors ||
    !networkError ||
    !networkError.message.match(/status code 401$/)
  ) {
    return
  }
  window.location.href = `https://platform.caroda.io/api/login?redirect=${getEncodedOrigin()}`
})

const client = new ApolloClient({
  link: errorLink.concat(httpLink),
  cache: new InMemoryCache({
    addTypename: false,
    // This could be dynamic, as per the official documentation:
    // https://www.apollographql.com/docs/react/v2/data/fragments/
    // However, unless the amount of fragments increase, I think this is simpler.
    fragmentMatcher: new IntrospectionFragmentMatcher({
      introspectionQueryResultData: {
        __schema: {
          types: [
            {
              kind: 'UNION',
              name: 'GraphQLExperimentInventory',
              possibleTypes: [
                {
                  name: 'GraphQLExperimentInventoryAccount',
                },
                {
                  name: 'GraphQLExperimentInventoryDomain',
                },
                {
                  name: 'GraphQLExperimentInventoryGroup',
                },
              ],
            },
          ],
        },
      },
    }),
  }),
})

async function ensureAuthentication() {
  try {
    const res = await client.query({
      query: gql`
        {
          session {
            email
            accountDisplayName
          }
        }
      `,
      fetchPolicy: 'network-only',
    })
    const { email, accountDisplayName } = res.data.session
    loadContentPlayer()
    authenticateToBeacon(email, accountDisplayName)
  } catch (e) {
    if ((e as Error).message.match(/status code 401$/)) {
      window.location.href = `https://platform.caroda.io/api/login?redirect=${getEncodedOrigin()}`
      await new Promise(() => {})
    }
    throw e
  }
}

function authenticateToBeacon(email: string, name: string) {
  ;(window as any).Beacon('identify', { name, email })
}

async function loadContentPlayer() {
  const today = new Date().toISOString().split('T')[0]
  const contentPlayerScriptSource = `https://ads.caroda.io/videoPlayer/v/${today}/s/2/contentPlayer.min.js`
  const postscribe = (window as any).postscribe as any
  postscribe(
    document.head,
    `<script crossorigin="anonymous" src="${contentPlayerScriptSource}"></script>`
  )
}

async function logout() {
  await ensureAuthentication()
  const logoutUrl = `https://platform.caroda.io/api/logout?redirect=${getEncodedOrigin()}`
  window.location.href = logoutUrl
}

function removeTypenames(o: any) {
  if (!o || typeof o !== 'object') {
    return
  }
  delete o.__typename
  for (const k of Object.keys(o)) {
    removeTypenames(o[k])
  }
}

export { client, ensureAuthentication, logout, removeTypenames }
